import { X } from 'lucide-react';
import type { SegmentInterpretation, VoterDataFieldDto } from '@signalvote/shared';
import { Button } from '@/components/ui/button';

/**
 * "How I read that": what a plain-English request was applied as. Each field it set and how it was
 * read, notes such as an age rounded to L2's bands, and what it left out ("churchgoing: no field,
 * use Religion", where the stand-in field opens from the flag). The request is already applied —
 * there is no confirm step — so Undo is the way back. Undo is only offered while the list is still
 * as the request left it, so it can never throw away a change made by hand since.
 */
export function VoterDataReading({
  text,
  result,
  fields,
  canUndo,
  onUndo,
  onDismiss,
  onOpenField,
}: {
  text: string;
  result: SegmentInterpretation;
  fields: VoterDataFieldDto[];
  canUndo: boolean;
  onUndo: () => void;
  onDismiss: () => void;
  onOpenField: (field: VoterDataFieldDto) => void;
}) {
  return (
    <section aria-label="How I read that" className="mt-5 rounded-xl border bg-muted/40 px-4 py-3 text-sm">
      <div className="flex items-start gap-3">
        <p className="flex-1 font-medium text-foreground">How I read “{text}”</p>
        {canUndo && (
          <Button variant="outline" size="sm" onClick={onUndo}>
            Undo
          </Button>
        )}
        <button
          type="button"
          aria-label="Dismiss"
          onClick={onDismiss}
          className="inline-flex size-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
        >
          <X className="size-4" aria-hidden="true" />
        </button>
      </div>
      {result.summary.length > 0 ? (
        <ul className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-muted-foreground">
          {result.summary.map((line) => (
            <li key={line.fieldId}>
              <span className="font-semibold text-foreground">{line.field}:</span> {line.reading}
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-2 text-muted-foreground">I found nothing to add from that. Try naming a field, or search for one.</p>
      )}
      {result.notes.map((note) => (
        <p key={note} className="mt-2 text-muted-foreground">
          {note}
        </p>
      ))}
      {result.gaps.map((gap) => {
        const useField = fields.find((f) => f.id === gap.useFieldId);
        return (
          <p key={`${gap.phrase}:${gap.reason}`} className="mt-2 text-muted-foreground">
            <span className="text-foreground">“{gap.phrase}”</span>: {gap.reason}
            {useField && (
              <>
                , use{' '}
                <button type="button" onClick={() => onOpenField(useField)} className="font-semibold text-primary underline-offset-2 hover:underline">
                  {useField.label}
                </button>
              </>
            )}
          </p>
        );
      })}
    </section>
  );
}
